/**
 * Enterprise page - Redesigned following Cursor's structure
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {Button} from '../../components/ui/Button';
import {TestimonialCard} from '../../components/landing/TestimonialCard';
import {CompanyLogoGrid} from '../../components/landing/CompanyLogoGrid';
import {StatCard} from '../../components/landing/StatCard';
import {SecurityFeatureGrid} from '../../components/landing/SecurityFeatureGrid';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

// Company logos for hero section
const heroCompanies = [
  {name: 'Samsung', initials: 'SA'},
  {name: 'OpenAI', initials: 'AI'},
  {name: 'Zoom', initials: 'ZO'},
  {name: 'Datadog', initials: 'DD'},
  {name: 'Adobe', initials: 'AD'},
  {name: 'Stripe', initials: 'ST'},
  {name: 'PwC', initials: 'PwC'},
  {name: 'Figma', initials: 'FI'},
  {name: 'Carlyle', initials: 'CA'},
  {name: 'Sysco', initials: 'SY'},
  {name: 'Deloitte', initials: 'DE'},
  {name: 'Shopify', initials: 'SH'},
  {name: 'eBay', initials: 'EB'},
  {name: 'British Petroleum', initials: 'BP'},
  {name: 'Mercado Libre', initials: 'ML'},
  {name: 'Sanofi', initials: 'SA'},
  {name: 'British Airways', initials: 'BA'},
  {name: 'Ramp', initials: 'RA'},
  {name: 'Rivian', initials: 'RI'},
  {name: 'Hilton', initials: 'HI'},
  {name: 'Fox', initials: 'FO'},
  {name: 'Budweiser', initials: 'BU'},
  {name: 'Lyft', initials: 'LY'},
];

// Testimonials
const testimonials = [
  {
    quote: "I've never, as a CTO, received so many texts or Slack messages from employees just saying 'Thank you for getting this technology here.'",
    author: 'Melody Hildebrandt',
    title: 'CTO',
    company: 'Fox',
    stats: undefined,
  },
  {
    quote: "Across roles and levels, we're seeing an increase of over 25% in meeting productivity and over 100% in action item completion. Together, that means we're making about 50% more progress in our meetings.",
    author: 'Anton Andreev',
    title: 'Principal Software Engineer',
    company: 'Upwork',
    stats: undefined,
  },
  {
    quote: "What used to be a weeks-long process, navigating complex meeting notes and scheduling follow-ups across global teams, is now a structured process built around VoxaPilot from day one.",
    author: 'Roni Avidov',
    title: 'Senior R&D Team Lead',
    company: 'Monday',
    stats: undefined,
  },
  {
    quote: "VoxaPilot quickly grew from hundreds to thousands of extremely enthusiastic Stripe employees. We spend more on meetings and collaboration than any other undertaking, and there's significant economic outcomes when making that process more efficient and productive.",
    author: 'Patrick Collison',
    title: 'Co-Founder & CEO',
    company: 'Stripe',
    stats: undefined,
  },
  {
    quote: "My favorite enterprise meeting intelligence service is VoxaPilot. Every one of our engineers, some 40,000, are now assisted by AI meeting summaries and our productivity has gone up incredibly.",
    author: 'Jensen Huang',
    title: 'President & CEO',
    company: 'NVIDIA',
    stats: undefined,
  },
  {
    quote: "By February 2025, every Coinbase team had utilized VoxaPilot, which has become the preferred meeting tool for most of our developers. Single teams are now processing, analyzing, and acting on meeting insights in days instead of weeks.",
    author: 'Brian Armstrong',
    title: 'CEO',
    company: 'Coinbase',
    stats: undefined,
  },
  {
    quote: "VoxaPilot has transformed the way our engineering teams capture and act on meeting insights, with adoption growing from 150 to over 500 engineers (~60% of our org!) in just a few weeks.",
    author: 'Albert Strasheim',
    title: 'CTO',
    company: 'Rippling',
    stats: undefined,
  },
  {
    quote: "Watching dozens of action items get automatically extracted and assigned every day has become normal, and that freed-up velocity shows up everywhere from release cadence to decision-making speed. VoxaPilot isn't a convenience add-on; it's a scale-multiplier for the whole org.",
    author: 'Cody De Arkland',
    title: 'Senior Director',
    company: 'Sentry',
    stats: undefined,
  },
  {
    quote: "At Optiver, we've recently expanded our VoxaPilot usage to firm-wide deployment. Our teams have consistently found VoxaPilot to be robust, context-aware, and flexible enough to support the scale and complexity of a global trading firm.",
    author: 'Scott McKenzie',
    title: 'Head of Engineering',
    company: 'Optiver',
    stats: undefined,
  },
];

// Security features
const securityFeatures = [
  {
    title: 'Dedicated guidance',
    description: 'Deploy meeting intelligence at scale with professional expertise and dedicated account management',
  },
  {
    title: 'Premium support',
    description: 'Tailored support for teams with specialized needs, including 24/7 priority support and dedicated success managers',
  },
  {
    title: 'Zero data retention',
    description: 'No training on your data by VoxaPilot or AI providers. Your meeting data remains private and is never used to train models',
  },
  {
    title: 'Identity management',
    description: 'SAML-based SSO integration for secure user access with support for all major identity providers',
  },
  {
    title: 'SCIM user provisioning',
    description: 'Easily create, update, and remove users and groups automatically through your identity provider',
  },
  {
    title: 'Centralized security controls',
    description: 'Configure model access, privacy settings, and retention policies across your entire organization from a single dashboard',
  },
  {
    title: 'Global compliance standards',
    description: 'Compliant with GDPR, CCPA, HIPAA, and SOC 2 Type 2. Meet regulatory requirements across all regions',
  },
  {
    title: 'Third-party security certifications',
    description: 'SOC 2 Type 2 certified with annual penetration testing and continuous security monitoring',
  },
  {
    title: 'Robust data protection',
    description: 'AES-256 encryption at rest and TLS 1.2+ in transit. All data is encrypted end-to-end with enterprise-grade security',
  },
];

export function EnterprisePage() {
  return (
    <LandingLayout>
      {/* Hero Section */}
      <Section padding="lg" background="white" className="pt-32 pb-20 md:pb-24 lg:pb-32 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-screen-2xl">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-6">
              Transform enterprise meetings at scale
            </h1>
            <p className="text-xl md:text-2xl text-textSecondary dark:text-[#858585] mb-12 max-w-3xl mx-auto leading-relaxed">
              AI-powered meeting intelligence for the world's leading organizations
            </p>
            <div className="mb-20">
              <Link to={ROUTES.CONTACT}>
                <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white">
                  Contact sales
                </Button>
              </Link>
            </div>
            <div>
              <p className="text-base md:text-lg text-textSecondary dark:text-[#858585] mb-8 text-center">
                Trusted by the world's leading enterprises
              </p>
              <CompanyLogoGrid companies={heroCompanies} />
            </div>
          </div>
        </Container>
      </Section>

      {/* Product Demo Section */}
      <ProductDemoSection />

      {/* Value Proposition Section */}
      <Section padding="lg" background="gray" className="py-20 md:py-28 bg-backgroundSecondary dark:bg-[#252526]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="Ship better decisions, faster"
            subtitle="VoxaPilot helps your entire organization make meetings more productive"
          />
          <div className="mt-16">
            <div className="max-w-4xl mx-auto">
              <TestimonialCard {...testimonials[0]} />
            </div>
          </div>
        </Container>
      </Section>

      {/* Enterprise Knowledge Base Section */}
      <Section padding="lg" background="white" className="py-20 md:py-28 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-6xl">
          <div>
            <EnterpriseSectionHeader
              title="Your meeting intelligence hub"
              subtitle="Built for complex organizations with thousands of meetings"
              align="left"
            />
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12 mb-16">
              <FeatureCard
                title="Centralized meeting repository"
                description="All your organization's meetings in one searchable, secure location. Access transcripts, summaries, and insights from any device, anywhere. Enterprise-grade search capabilities let you find specific discussions, decisions, or action items across your entire meeting history."
              />
              <FeatureCard
                title="Cross-team collaboration"
                description="Share insights and action items across departments seamlessly. Enable teams to discover relevant meetings, collaborate on action items, and maintain context across organizational boundaries. Built-in permissions ensure the right people have access to the right information."
              />
              <FeatureCard
                title="Historical meeting search"
                description="Find any discussion, decision, or insight from past meetings instantly. Advanced search capabilities with filters for date ranges, participants, topics, and keywords. Never lose track of important decisions or commitments made in meetings."
              />
              <FeatureCard
                title="Organization-wide insights"
                description="Analytics and trends across all teams and departments. Understand meeting patterns, identify bottlenecks, and optimize collaboration. Track key metrics like meeting frequency, duration, participation rates, and action item completion across your organization."
              />
            </div>
            <div className="mt-16">
              <div className="max-w-4xl">
                <TestimonialCard {...testimonials[1]} />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Adoption & Satisfaction Section */}
      <Section padding="lg" background="gray" className="py-20 md:py-28 bg-backgroundSecondary dark:bg-[#252526]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="Loved by teams"
            subtitle="In enterprise evaluations, 93% of organizations choose VoxaPilot"
          />
          <div className="mt-16">
            <div className="max-w-4xl mx-auto">
              <TestimonialCard {...testimonials[2]} />
            </div>
          </div>
        </Container>
      </Section>

      {/* Powerful & Customizable Section */}
      <Section padding="lg" background="white" className="py-20 md:py-28 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="Powerful, yet customizable"
            subtitle="Standardize your organization on the same meeting intelligence tools"
          />
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-12">
            {/* Total Control Card */}
            <div className="bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-2xl p-8 md:p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">Total control</h3>
              <p className="text-textSecondary dark:text-[#858585] mb-6 leading-relaxed">
                Globally configure transcription settings, AI models, and privacy controls. Set organization-wide policies for data retention, access controls, and compliance requirements. Manage all settings from a centralized admin dashboard.
              </p>
              <Link to="/docs/configuration/settings" className="text-sm font-medium text-orange-600 dark:text-orange-500 hover:underline inline-flex items-center gap-1">
                View enterprise controls →
              </Link>
            </div>

            {/* Access the Best Models Card */}
            <div className="bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-2xl p-8 md:p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">Access the best models</h3>
              <p className="text-textSecondary dark:text-[#858585] mb-6 leading-relaxed">
                Choose between frontier AI models from OpenAI, Anthropic, Gemini, and more. Select the best model for each use case, or let VoxaPilot automatically route to the optimal model based on meeting context and requirements.
              </p>
              <Link to="/docs/models" className="text-sm font-medium text-orange-600 dark:text-orange-500 hover:underline inline-flex items-center gap-1">
                View available models →
              </Link>
            </div>

            {/* Ship with Confidence Card */}
            <div className="bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-2xl p-8 md:p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">Ship with confidence</h3>
              <p className="text-textSecondary dark:text-[#858585] mb-6 leading-relaxed">
                Use VoxaPilot to ensure meeting compliance and data governance. Built-in audit logs, retention policies, and compliance reporting help you meet regulatory requirements and maintain governance standards across your organization.
              </p>
              <Link to={ROUTES.SECURITY} className="text-sm font-medium text-orange-600 dark:text-orange-500 hover:underline inline-flex items-center gap-1">
                Learn more about compliance →
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* Impact Statistics Section */}
      <Section padding="lg" background="gray" className="py-20 md:py-28 bg-backgroundSecondary dark:bg-[#252526]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="AI is changing how meetings are conducted"
            subtitle="Join thousands of organizations transforming their meeting culture"
          />
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-12">
            <StatCard number="64%" description="Fortune 500 companies using VoxaPilot" />
            <StatCard number="50,000+" description="Enterprises choose to build with VoxaPilot" />
            <StatCard number="100M+" description="Minutes of meetings transcribed per day with VoxaPilot" />
          </div>
        </Container>
      </Section>

      {/* Security & Compliance Section */}
      <Section padding="lg" background="white" className="py-20 md:py-28 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="Trusted by companies worldwide"
            subtitle="Built with security and compliance at the core"
          />
          <div className="mt-12">
            <SecurityFeatureGrid features={securityFeatures} />
          </div>
          <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTES.SECURITY}>
              <Button variant="outline" size="lg" className="px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2D2D30]">
                Visit our Trust Center ↗
              </Button>
            </Link>
            <Link to={ROUTES.SECURITY}>
              <Button variant="outline" size="lg" className="px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2D2D30]">
                Read about our security →
              </Button>
            </Link>
          </div>
        </Container>
      </Section>

      {/* Enterprise Testimonials Section */}
      <Section padding="lg" background="gray" className="py-20 md:py-28 bg-backgroundSecondary dark:bg-[#252526]">
        <Container className="max-w-6xl">
          <EnterpriseSectionHeader
            title="Modern organizations are powered by VoxaPilot"
            subtitle="See how leading companies transform their meeting culture"
          />
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12">
            {testimonials.slice(3).map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Final CTA Section */}
      <Section padding="lg" background="white" className="py-20 md:py-28 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Bring VoxaPilot to your team
            </h2>
            <p className="text-xl text-textSecondary dark:text-[#858585] mb-10">
              Contact our sales team to learn more about enterprise plans
            </p>
            <Link to={ROUTES.CONTACT}>
              <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white px-8 py-4 rounded-lg">
                Contact sales →
              </Button>
            </Link>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

// Helper Components
function EnterpriseSectionHeader({title, subtitle, align = 'center'}: {title: string; subtitle: string; align?: 'center' | 'left'}) {
  const {ref, isVisible} = useScrollAnimation();
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className={`${align === 'center' ? 'text-center' : 'text-left'} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} transition-all duration-700`}>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
        {title}
      </h2>
      <p className="text-xl md:text-2xl text-textSecondary dark:text-[#858585] max-w-3xl mx-auto">
        {subtitle}
      </p>
    </div>
  );
}

function FeatureCard({title, description}: {title: string; description: string}) {
  const {ref, isVisible} = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <h3 className="text-lg md:text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3">
        {title}
      </h3>
      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function ProductDemoSection() {
  const {ref, isVisible} = useScrollAnimation();
  return (
    <Section padding="lg" background="gray" className="py-16 md:py-20 bg-backgroundSecondary dark:bg-[#252526]" ref={ref as React.RefObject<HTMLElement>}>
      <Container className="max-w-7xl">
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
            {/* Browser Chrome */}
            <div className="bg-gray-100 dark:bg-[#252526] border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-2">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 bg-white dark:bg-[#1E1E1E] rounded-md px-4 py-1.5 mx-4 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-textTertiary dark:text-[#6A6A6A]">app.voxapilot.com/enterprise</div>
              </div>
            </div>

            {/* Demo Content */}
            <div className="p-8 lg:p-12 bg-white dark:bg-[#1E1E1E]">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">Enterprise Dashboard</h3>
                  <p className="text-base text-textSecondary dark:text-[#858585] mb-6 leading-relaxed">
                    Centralized control and visibility across your entire organization. Monitor usage, manage teams, and configure security settings from one powerful interface.
                  </p>
                  <div className="space-y-3">
                    {[
                      'Organization-wide analytics',
                      'Team management & permissions',
                      'Security & compliance controls',
                      'Usage insights & reporting',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-orange-600 dark:bg-orange-500 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-sm text-textSecondary dark:text-[#858585]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-[#252526] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-textSecondary dark:text-[#858585]">Active Teams</span>
                      <span className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC]">247</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-[#1E1E1E] rounded-full overflow-hidden">
                      <div className="h-full bg-orange-600 dark:bg-orange-500 rounded-full" style={{width: '82%'}}></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4">
                      <div className="text-center">
                        <div className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">12.4K</div>
                        <div className="text-xs text-textSecondary dark:text-[#858585]">Meetings</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">98.2%</div>
                        <div className="text-xs text-textSecondary dark:text-[#858585]">Accuracy</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">3.2K</div>
                        <div className="text-xs text-textSecondary dark:text-[#858585]">Users</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
